import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ACTIONS_COL_WIDTH,
  Alert,
  Badge,
  Button,
  DataTable,
  OverlayTrigger,
  PageHeader,
  Tooltip,
  DecisionFeedback,
  DECISION_FEEDBACK_MS,
  EmptyValue,
  type DecisionResult,
} from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { notifyApprovalsChanged } from '../../../components/awaitingApprovalsCount';
import { ExpiryIndicator } from '../../../components/ExpiryIndicator';
import { ResourceRate, Vendor, vendorService } from '../../../services/vendorService';
import { enteredByLabel, shortDate } from '../../vendors/utils/format';
import { rateChangeSummary, requestAge } from '../../vendors/utils/rateChange';
import RateDecisionModal, { RateDecision } from '../../vendors/components/RateDecisionModal';
import PossibleMatches from '../../vendors/components/PossibleMatches';
import { confirmAction } from '@/components/ui';

/**
 * Vendor Approvals (was "Vendor Admin", /he-admin) - only what is waiting on a second person:
 * vendors awaiting activation, and rate requests awaiting approval, first rates included (H6).
 * The all-vendor resource catalogue moved to Admin > Procurement & Materials > Resources (L1).
 *
 * Opening the screen requires heMaster:vendor_approve. Activating needs vendor_activate and
 * approving or rejecting a rate needs rate_approve; someone holding only the screen key sees
 * the queue without the buttons. A requester can never approve their own rate - their own
 * rows offer Withdraw instead (M5).
 */
const HeAdminPage: React.FC = () => {
  useDocumentTitle('Vendor Approvals');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [pendingVendors, setPendingVendors] = useState<Vendor[]>([]);
  const [pendingRates, setPendingRates] = useState<ResourceRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [decision, setDecision] = useState<{ rate: ResourceRate; kind: RateDecision } | null>(null);

  const canActivate = hasPermission('heMaster:vendor_activate');
  const canApproveRates = hasPermission('heMaster:rate_approve');
  const canEditRates = hasPermission('heMaster:rate_edit');
  const currentUserId = currentUser?.id;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [vendors, rates] = await Promise.all([
        vendorService.listPendingActivation(),
        vendorService.listPendingRates(),
      ]);
      setPendingVendors(vendors);
      setPendingRates(rates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // V2-013 / STATE-09: the decision's result shows on its row for about 5 s, then the row leaves.
  // A failure stays on the row with Retry. One behaviour with Submissions (DecisionFeedback).
  const [rowResults, setRowResults] = useState<Map<string, DecisionResult>>(new Map());
  const rowTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const setRowResult = (key: string, result: DecisionResult | null) =>
    setRowResults((prev) => {
      const next = new Map(prev);
      if (result) next.set(key, result);
      else next.delete(key);
      return next;
    });
  useEffect(() => {
    const timers = rowTimers.current;
    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  const afterChange = async (key: string, message: string) => {
    showToast(message, 'success');
    // The navbar badge refetches now rather than on the next navigation (M3).
    notifyApprovalsChanged();
    setRowResult(key, { tone: 'success', message });
    const timer = setTimeout(() => {
      rowTimers.current.delete(key);
      setRowResult(key, null);
      void load();
    }, DECISION_FEEDBACK_MS);
    rowTimers.current.set(key, timer);
  };

  const handleActivate = async (v: Vendor) => {
    if (!(await confirmAction({ title: `Activate "${v.name}"?`, body: 'It becomes available for work in its service areas.', confirmLabel: 'Activate' }))) return;
    const key = `vendor-${v.id}`;
    try {
      setActionError(null);
      setRowResult(key, null);
      await vendorService.activateVendor(v.id);
      await afterChange(key, `${v.name} activated – moving to Active`);
    } catch (err) {
      setRowResult(key, {
        tone: 'error',
        message: err instanceof Error ? err.message : 'Failed to activate vendor',
        onRetry: () => void handleActivate(v),
      });
    }
  };

  const handleRateDecision = async (rate: ResourceRate, kind: RateDecision, comments?: string) => {
    const name = rate.resource?.name ?? 'Rate';
    if (kind === 'approve') await vendorService.approveRate(rate.id);
    else if (kind === 'reject') await vendorService.rejectRate(rate.id, comments);
    else await vendorService.deleteRate(rate.id);
    setDecision(null);
    await afterChange(
      `rate-${rate.id}`,
      kind === 'approve' ? `${name}: rate approved` : kind === 'reject' ? `${name}: rate rejected` : `${name}: request withdrawn`
    );
  };

  const vendorColumns: DataTableColumn<Vendor>[] = [
    {
      key: 'name',
      header: 'Vendor',
      mobile: 'title',
      cell: (v) => (
        <span className="tw-inline-flex tw-flex-col">
          <Link to={`/vendors/${v.id}`} onClick={(e) => e.stopPropagation()}>
            {v.name}
          </Link>
          {v.legalName && v.legalName !== v.name && (
            <span className="tw-text-sm tw-text-content-muted">{v.legalName}</span>
          )}
        </span>
      ),
    },
    {
      key: 'serviceAreas',
      header: 'Service Areas',
      mobile: 'subtitle',
      cell: (v) => v.serviceAreas?.map((sa) => sa.serviceArea.name).join(', ') || '–',
    },
    {
      key: 'createdBy',
      header: 'Entered By',
      cell: (v) => (
        <span className="tw-inline-flex tw-flex-col">
          <span className={v.createdBy ? '' : 'tw-text-content-muted'}>{enteredByLabel(v)}</span>
          <span className="tw-text-sm tw-text-content-muted">{shortDate(v.createdAt)}</span>
        </span>
      ),
    },
    {
      key: 'compliance',
      header: 'Compliance',
      cell: (v) =>
        v.compliance?.hasIssue ? (
          <ExpiryIndicator compliance={v.compliance} showLabel />
        ) : (
          <span className="tw-text-success">
            <i className="bi bi-check-circle tw-mr-1" />
            Current
          </span>
        ),
    },
    {
      key: 'matches',
      header: 'Possible Duplicates',
      mobile: 'meta',
      cell: (v) => <PossibleMatches matches={v.possibleMatches} reason={v.duplicateOverrideReason} compact />,
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: ACTIONS_COL_WIDTH,
      mobile: 'trailing',
      cell: (v) => {
        if (!canActivate) return null;
        const blockers = v.activationBlockers ?? [];
        if (blockers.length) {
          // M14b: disabled with the reason, not hidden.
          return (
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip id={`activate-blocked-${v.id}`}>
                  Cannot activate yet:
                  <ul className="tw-mb-0 tw-pl-4">
                    {blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </Tooltip>
              }
            >
              <span className="tw-inline-block" tabIndex={0} aria-label={`Cannot activate: ${blockers.join('; ')}`}>
                <Button variant="primary" size="sm" disabled>
                  Activate
                </Button>
              </span>
            </OverlayTrigger>
          );
        }
        return (
          <Button variant="primary" size="sm" onClick={() => handleActivate(v)}>
            Activate
          </Button>
        );
      },
    },
  ];

  const rateColumns: DataTableColumn<ResourceRate>[] = [
    {
      key: 'resource',
      header: 'Resource',
      mobile: 'title',
      cell: (r) => (
        <span className="tw-inline-flex tw-flex-col">
          <span>{r.resource?.name ?? r.resourceId}</span>
          {r.resource?.vendor && (
            <Link
              to={`/vendors/${r.resource.vendor.id}?tab=resources`}
              className="tw-text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              {r.resource.vendor.name}
            </Link>
          )}
        </span>
      ),
    },
    { key: 'serviceArea', header: 'Service Area', mobile: 'subtitle', cell: (r) => r.serviceArea?.name ?? '–' },
    {
      key: 'change',
      header: 'Change',
      cell: (r) => {
        const s = rateChangeSummary(r);
        return (
          <span className="tw-inline-flex tw-flex-col tw-tabular-nums">
            {s.isNew ? (
              <span>
                <Badge bg="info" className="tw-mr-1">
                  New rate
                </Badge>
                <strong>{s.text.replace(/^new rate /, '')}</strong>
              </span>
            ) : (
              <span>{s.text}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'effective',
      header: 'Effective',
      cell: (r) => (
        <span>
          {shortDate(r.effectiveFrom)}
          {r.effectiveTo ? ` – ${shortDate(r.effectiveTo)}` : ' onward'}
        </span>
      ),
    },
    {
      key: 'requested',
      header: 'Requested',
      cell: (r) => (
        <span className="tw-inline-flex tw-flex-col">
          <span>{r.requestedBy ? `${r.requestedBy.name} ${r.requestedBy.lastName}` : '–'}</span>
          <span className="tw-text-sm tw-text-content-muted">
            {shortDate(r.createdAt)} · waiting {requestAge(r.createdAt)}
          </span>
        </span>
      ),
    },
    // V2-054: one Reason column – the explanation WRAP-2007 made required. It used to appear
    // twice, as Comment and as Reason, with the same text.
    {
      key: 'reason',
      header: 'Reason',
      mobile: 'meta',
      cell: (r) =>
        r.comments ? (
          <span title={r.comments} className="tw-line-clamp-2 tw-max-w-xs tw-whitespace-pre-line">
            {r.comments}
          </span>
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: 'agreement',
      header: 'Agreement',
      cell: (r) =>
        r.agreementDocument ? (
          <a
            href={vendorService.getDocumentDownloadUrl(r.agreementDocument.id)}
            target="_blank"
            rel="noopener noreferrer"
            title={r.agreementDocument.fileName}
          >
            <i className="bi bi-paperclip tw-mr-1" />
            {r.agreementDocument.fileName}
          </a>
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: ACTIONS_COL_WIDTH,
      mobile: 'trailing',
      cell: (r) => {
        const isOwn = !!currentUserId && r.requestedById === currentUserId;
        if (isOwn) {
          // M5: the requester cannot approve their own request, but can take it back.
          return canEditRates ? (
            <Button variant="outline-danger" size="sm" onClick={() => setDecision({ rate: r, kind: 'withdraw' })}>
              Withdraw
            </Button>
          ) : (
            <span className="tw-text-sm tw-text-content-muted">Your request</span>
          );
        }
        if (!canApproveRates) return null;
        return (
          <div className="tw-flex tw-justify-end tw-gap-2">
            <Button variant="primary" size="sm" onClick={() => setDecision({ rate: r, kind: 'approve' })}>
              Approve Rate
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => setDecision({ rate: r, kind: 'reject' })}>
              Reject Rate
            </Button>
          </div>
        );
      },
    },
  ];

  const approvalCount = pendingVendors.length + pendingRates.length;

  return (
    <div className="he-admin-page tw-p-3 md:tw-p-4">
      <PageHeader
        title="Vendor Approvals"
        actions={[
          {
            key: 'vendors',
            label: 'All Vendors',
            icon: 'bi-building',
            intent: 'secondary',
            onClick: () => navigate('/vendors'),
          },
          {
            key: 'refresh',
            label: 'Refresh',
            icon: 'bi-arrow-clockwise',
            // Refresh is chrome, not an outcome - neutral, like WorkIdsPage and ProjectsView.
            intent: 'neutral',
            onClick: load,
          },
        ]}
      />

      {error && <Alert variant="danger" className="tw-mb-3">{error}</Alert>}
      {actionError && <Alert variant="danger" className="tw-mb-3">{actionError}</Alert>}

      {!canActivate && !canApproveRates && approvalCount > 0 && (
        <Alert variant="info" className="tw-mb-3">
          There {approvalCount === 1 ? 'is' : 'are'} {approvalCount} item
          {approvalCount === 1 ? '' : 's'} awaiting approval. You can see the queue but not approve
          from it – approval is a separate permission held by a second person.
        </Alert>
      )}

      <h2 className="tw-mb-2 tw-text-base tw-font-semibold">
        Vendors awaiting activation ({pendingVendors.length})
      </h2>
      <DataTable
        columns={vendorColumns}
        data={pendingVendors}
        rowKey={(v) => v.id}
        loading={loading}
        framed
        onRowClick={(v) => navigate(`/vendors/${v.id}`)}
        isExpanded={(v) => rowResults.has(`vendor-${v.id}`)}
        renderExpanded={(v) => <DecisionFeedback result={rowResults.get(`vendor-${v.id}`)!} />}
        emptyMessage="No vendors are waiting for activation."
        className="tw-mb-4"
      />

      <h2 className="tw-mb-2 tw-text-base tw-font-semibold">
        Rates awaiting approval ({pendingRates.length})
      </h2>
      <DataTable
        columns={rateColumns}
        data={pendingRates}
        rowKey={(r) => r.id}
        loading={loading}
        framed
        isExpanded={(r) => rowResults.has(`rate-${r.id}`)}
        renderExpanded={(r) => <DecisionFeedback result={rowResults.get(`rate-${r.id}`)!} />}
        emptyMessage="No rates are waiting for approval."
      />

      <RateDecisionModal
        rate={decision?.rate ?? null}
        decision={decision?.kind ?? null}
        onCancel={() => setDecision(null)}
        onConfirm={handleRateDecision}
      />
    </div>
  );
};

export default HeAdminPage;
